import { useState } from 'react';
import { VaultMatterModel } from '../../../api/vault-api';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useVaultActions } from './hooks/use-vault-actions';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface EditVaultMatterDialogProps {
  open: boolean;
  handleClose: () => void;
  setSelectedMatterId: (id: string) => void;
  matter: VaultMatterModel;
}

export default function EditVaultMatterDialog({
  open,
  handleClose,
  setSelectedMatterId,
  matter,
}: EditVaultMatterDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { changeLogRoles } = useUserSettings();

  const [name, setName] = useState(matter.name || '');
  const [description, setDescription] = useState(matter.description || '');

  const { updateMatter, isUpdatingMatter } = useVaultActions();

  const onSubmit = () => {
    if (
      isAbleToSeeUIElement(changeLogRoles, VaultRoles.EditVaultMatters.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      updateMatter(
        { matterId: matter.matterId, name, description, changeLogMessage },
        {
          onSuccess: (data) => {
            if (data && data.matterId) {
              setSelectedMatterId(data.matterId);
            }
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
            handleCloseDialog();
          },
        }
      );
    }
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleCloseDialog}
        title="Edit Vault Matter"
        maxWidth="sm"
        dialogContent={
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Matter Name"
              fullWidth
              required
              autoFocus
            />
            <Stack>
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                label="Description"
                fullWidth
              />
            </Stack>
          </Stack>
        }
        primaryButton={{
          title: 'Save Changes',
          action: onSubmit,
          disabled: isUpdatingMatter || !name.trim(),
          loading: isUpdatingMatter,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          updateMatter(
            { matterId: matter.matterId, name, description, changeLogMessage },
            {
              onSuccess: (data) => {
                if (data && data.matterId) {
                  setSelectedMatterId(data.matterId);
                }
                setOpenChangeLogDialog(false);
                setChangeLogMessage('');
                handleCloseDialog();
              },
            }
          );
        }}
        mutateLoading={isUpdatingMatter}
      />
    </>
  );
}
